import {
  message,
  Modal,
  notification,
  Popover,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { PolicyBenefitItem, PolicyBenefitMaterial } from './types';
import ToReceiveColumnTitle from './ToReceiveColumnTitle';
import RemoveBenefitTable from './RemoveBenefitTable';
import { PortoPolicyBenefit } from '@/core/domain/porto/policies/porto-policies.types';
import ComplementPolicyBenefitsHeader from './BenefitsHeader';
import BenefitMaterialSelect from './BenefitMaterialSelect';
import { BenefitConsumable } from '@/core/domain/operational/benefits-consumable/benefits-consumable.types';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { Group, InputDecimal } from '@/components/_common';
import {
  useWorkOrderItemBenefitsCreate,
  useWorkOrderItemBenefitsRemove,
  useWorkOrderItems,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import AddCourtesyBenefitTable from './AddCourtesyBenefitTable';
import AddBenefitTable from './AddBenefitTable';
import StatusTag from '@/components/_common/StatusTag';
import {
  WorkOrderComplementStatus,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { AxiosError } from 'axios';
import ActionIcon from '@/components/_common/ActionIcon';
import { ExternalLink } from 'react-feather';
import SubGroupsSelectField from '@/components/_common/SubGroupsSelectField';

import './style.less';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

type ErrorType = {
  manufacturerId: boolean;
  quantity: boolean;
  subGroupId: boolean;
  isConsumableRequired: boolean;
}[][];

export default function ComplementPolicyBenefits() {
  const [benefits, setBenefits] = useState<PolicyBenefitItem[]>([]);
  const benefitIds = benefits.map((benefit) => benefit.sequencialNumber);

  const [withCoutesy, setWithCourtesy] = useState(false);

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isCustomerTypeInsurance =
    complement?.customerType === CustomerType.Insurance;
  const isCourtesyOrReturn =
    complement?.type === WorkOrderComplementType.Courtesy ||
    complement?.type === WorkOrderComplementType.Return;

  const benefitDisabled = isCustomerTypeInsurance || isCourtesyOrReturn;

  const isClosedOrCanceled =
    complement?.status === WorkOrderComplementStatus.Closed ||
    complement?.status === WorkOrderComplementStatus.Cancelled;

  const [expandedRowKey, setExpandedRowKey] = useState<number>();

  const [errors, setErrors] = useState<ErrorType>([]);

  const creteItemsForBenefits = useWorkOrderItemBenefitsCreate();

  const benefitRemove = useWorkOrderItemBenefitsRemove();

  const workOrderItems = useWorkOrderItems(
    complement?.id,
    false,
    undefined,
    false,
  );
  const itemsInComplement = workOrderItems.data?.data || [];

  useEffect(() => {
    if (workOrderItems.isLoading) return;

    if (!itemsInComplement.length) {
      setBenefits((prev) =>
        prev.filter((benefit) => !benefit.complementItemId),
      );
    }

    const itemsWithBenefit = itemsInComplement.filter(
      (item) => !!item.benefitId,
    );

    const itemsService = itemsWithBenefit.filter(
      (item) => item.type === WorkOrderItemType.Service,
    );

    const benefitsInItems: PolicyBenefitItem[] = itemsService.map(
      (service, benefitIndex) => {
        const itemsPart = itemsWithBenefit.filter(
          (item) =>
            item.benefitId === service.benefitId &&
            item.type === WorkOrderItemType.Part,
        );

        return {
          name: service.description,
          sequencialNumber: Number(service.benefitId),
          serviceCode: Number(service.benefitServiceCode),
          complementItemId: service.id,
          isCourtesy: !!service.isCourtesy,
          disabled: !!service.benefitDateUsed,
          isConsumableRequired: !!itemsPart.length,
          materials: itemsPart
            .map<PolicyBenefitMaterial>((part) => ({
              serviceCode: Number(part.benefitServiceCode),
              benefitIndex,
              complementItemId: part.id,
              id: part.benefitMaterialId,
              manufacturerId: part.manufacturer.id,
              name: part.description,
              quantity: part.quantity,
              disabled: !!service.benefitDateUsed,
              isInternal: part.isInternal,
              subGroupId: part.subGroupId,
            }))
            .concat({
              name: 'insert-material',
              serviceCode: Number(service.benefitServiceCode),
              benefitIndex,
            } as PolicyBenefitMaterial),
        };
      },
    );

    if (benefitsInItems.length) {
      setBenefits(benefitsInItems);
    } else {
      setBenefits((prev) =>
        prev.filter((benefit) => !benefit.complementItemId),
      );
    }
  }, [
    itemsInComplement,
    workOrderItems.dataUpdatedAt,
    workOrderItems.isLoading,
  ]);

  const addBenefit = (
    benefit: (PortoPolicyBenefit & { isCourtesy: boolean }) | undefined,
    benefitIndex: number,
  ) => {
    setBenefits((prev) => {
      if (benefit) {
        return [
          ...prev,
          {
            disabled: false,
            ...benefit,
            materials: [
              {
                disabled: false,
                benefitIndex,
                serviceCode: benefit.serviceCode,
                name: 'insert-material',
                isInternal: false,
              },
            ],
          },
        ];
      }

      return prev;
    });
    setExpandedRowKey(benefit?.sequencialNumber);
  };

  const addMaterial = (
    material: BenefitConsumable | undefined,
    benefitIndex: number,
  ) => {
    setBenefits((prev) => {
      if (material) {
        return prev.map((benefit, index) => {
          if (index === benefitIndex) {
            const prevMaterials = benefit.materials || [];

            const insertMaterial = prevMaterials.pop();

            const materials = [
              ...prevMaterials,
              {
                disabled: false,
                benefitIndex,
                serviceCode: benefit.serviceCode,
                id: material?.id,
                manufacturerId: material?.manufacturer?.id,
                name: material?.name,
                unitCost: material?.unitCost,
                isInternal: material.isInternal,
              },
            ];

            if (insertMaterial) {
              materials.push(insertMaterial);
            }

            return { ...benefit, materials };
          }

          return benefit;
        });
      }

      return prev;
    });
  };

  const clearErrorField = (params: {
    key: string;
    benefitIndex: number;
    materialIndex: number;
  }) => {
    setErrors((benefitErrors) =>
      benefitErrors.map((benefitError, benefitIndex) => {
        if (benefitIndex === params.benefitIndex) {
          return benefitError.map((materialErros, materialIndex) => {
            if (materialIndex === params.materialIndex) {
              return { ...materialErros, [params.key]: false };
            }
            return materialErros;
          });
        }
        return benefitError;
      }),
    );
  };

  const updateManufacturer = (params?: {
    manufacturerId?: number;
    benefitIndex: number;
    materialIndex: number;
  }) => {
    setBenefits((prev) => {
      if (params?.manufacturerId) {
        clearErrorField({
          key: 'manufacturerId',
          benefitIndex: params.benefitIndex,
          materialIndex: params.materialIndex,
        });

        return prev.map((benefit, benefitIndex) => {
          if (benefitIndex === params.benefitIndex) {
            return {
              ...benefit,
              materials: benefit.materials?.map((material, materialIndex) => {
                if (materialIndex === params.materialIndex) {
                  return { ...material, manufacturerId: params.manufacturerId };
                }
                return material;
              }),
            };
          }
          return benefit;
        });
      }

      return prev;
    });
  };

  const updateSubGroupId = (params?: {
    subGroupId?: number;
    benefitIndex: number;
    materialIndex: number;
  }) => {
    setBenefits((prev) => {
      if (params?.subGroupId) {
        clearErrorField({
          key: 'subGroupId',
          benefitIndex: params.benefitIndex,
          materialIndex: params.materialIndex,
        });

        return prev.map((benefit, benefitIndex) => {
          if (benefitIndex === params.benefitIndex) {
            return {
              ...benefit,
              materials: benefit.materials?.map((material, materialIndex) => {
                if (materialIndex === params.materialIndex) {
                  return { ...material, subGroupId: params.subGroupId };
                }
                return material;
              }),
            };
          }
          return benefit;
        });
      }

      return prev;
    });
  };

  const updateQuantity = (params: {
    quantity?: number;
    benefitIndex: number;
    materialIndex: number;
  }) => {
    setBenefits((prev) => {
      clearErrorField({
        key: 'quantity',
        benefitIndex: params.benefitIndex,
        materialIndex: params.materialIndex,
      });

      return prev.map((benefit, benefitIndex) => {
        if (benefitIndex === params?.benefitIndex) {
          return {
            ...benefit,
            materials: benefit.materials?.map((material, materialIndex) => {
              if (materialIndex === params?.materialIndex) {
                return { ...material, quantity: params?.quantity };
              }
              return material;
            }),
          };
        }
        return benefit;
      });
    });
  };

  const getMaterialIds = useCallback(
    (benefitIndex?: number) =>
      (typeof benefitIndex === 'number'
        ? benefits[benefitIndex]?.materials?.map(
            (material) => material.id as number,
          ) || []
        : []
      ).filter((materialId) => typeof materialId === 'number'),
    [benefits],
  );

  const removerBenefit = (benefitIndex: number) => {
    setErrors((benefitErrors) =>
      benefitErrors.filter((_, index) => index !== benefitIndex),
    );

    setBenefits((prev) => prev.filter((_, index) => index !== benefitIndex));
  };

  const removerMaterial = (params: {
    benefitIndex: number;
    materialIndex: number;
  }) => {
    setErrors((benefitErrors) =>
      benefitErrors.map((errors, errorIndex) => {
        if (errorIndex === params.benefitIndex) {
          return errors.filter((_, index) => index !== params.materialIndex);
        }
        return errors;
      }),
    );

    setBenefits((prev) =>
      prev.map((benefit, benefitIndex) => {
        if (benefitIndex === params.benefitIndex) {
          return {
            ...benefit,
            materials: benefit.materials?.filter(
              (_, materialIndex) => materialIndex !== params.materialIndex,
            ),
          };
        }
        return benefit;
      }),
    );
  };

  const confirmToRemove = (params: {
    benefitIndex: number;
    materialIndex?: number;
    complementItemId?: number;
  }) => {
    const hasMaterial = typeof params.materialIndex === 'number';

    if (typeof params.complementItemId === 'number' && complement?.id) {
      return Modal.confirm({
        title: `Remoção de ${hasMaterial ? 'material' : 'beneficio'}`,
        content: `Ao remover o ${
          hasMaterial ? 'material' : 'benefício'
        } não será possível restaurar.`,
        okText: 'Remover',
        okButtonProps: { danger: true, type: 'primary' },
        onOk: () =>
          benefitRemove.mutateAsync({
            complementId: complement.id,
            itemId: params.complementItemId as number,
          }),
      });
    }

    if (hasMaterial) {
      return removerMaterial({
        benefitIndex: params.benefitIndex,
        materialIndex: params.materialIndex as number,
      });
    }

    return removerBenefit(params.benefitIndex);
  };

  const onSubmit = async () => {
    const benefitErros =
      benefits
        .filter((benefit) => benefit.name !== 'insert-benefit')
        .map((benefit) => {
          const materialErrors =
            benefit.materials
              ?.filter((material) => material.name !== 'insert-material')
              ?.map((material) => ({
                manufacturerId: !material.manufacturerId,
                quantity: !(typeof material.quantity === 'number'),
                subGroupId: !material.subGroupId && !!material.isInternal,
                isConsumableRequired: false,
              })) || [];

          if (
            benefit.isConsumableRequired &&
            !benefit.materials?.filter(
              (material) => material.name !== 'insert-material',
            )?.length
          ) {
            return [
              {
                isConsumableRequired: true,
                manufacturerId: false,
                quantity: false,
                subGroupId: false,
              },
            ];
          }

          return materialErrors;
        }) || [];

    const hasBenefitErros = benefitErros
      .flatMap((benefit) => benefit)
      .some(
        (errors) =>
          errors?.manufacturerId ||
          errors?.quantity ||
          errors?.subGroupId ||
          errors?.isConsumableRequired,
      );

    if (hasBenefitErros) {
      setErrors(benefitErros);
      message.destroy();
      return message.error('Há campos não preenchidos');
    } else {
      setErrors([]);
    }

    try {
      if (complement?.id) {
        await creteItemsForBenefits.mutateAsync({
          complementId: complement.id,
          items: benefits.map((benefit) => ({
            benefitId: benefit.sequencialNumber,
            benefitServiceCode: String(benefit.serviceCode),
            complementItemId: benefit.complementItemId,
            unitCost: 0,
            name: benefit.name as string,
            isCourtesy: benefit.isCourtesy,
            materials: benefit.materials
              ?.filter((benefit) => benefit.name !== 'insert-material')
              .map((material) => ({
                complementItemId: material.complementItemId,
                id: material.id as number,
                name: material.name as string,
                unitCost: 0,
                quantity: material.quantity as number,
                manufacturerId: material.manufacturerId as number,
                isInternal: material.isInternal,
                subGroupId: material.subGroupId,
              })),
          })),
        });
      }
    } catch (err: unknown) {
      const error = err as AxiosError<
        | {
            fieldErrors?: Record<'Items', string[]>;
          }
        | undefined
      >;

      const items = error.response?.data?.fieldErrors?.Items.map(
        (description) => ({ description }),
      );

      if (items?.length) {
        notification.destroy();
        Modal.error({
          width: 700,
          title: 'Verifique os benefícios!',
          content: (
            <Table
              scroll={{ x: true }}
              dataSource={items}
              columns={[
                {
                  title: 'Descrição',
                  dataIndex: 'description',
                  render: (description: string) => {
                    const strgs = description?.split('|');

                    strgs.shift();

                    return strgs.join('|');
                  },
                },
                {
                  render: (_, item) => {
                    const description = item.description;

                    const [workOrderId, complementId] = description
                      ?.split('|')?.[0]
                      ?.split('/');

                    return (
                      <Tooltip title="Ver orçamento">
                        <ActionIcon
                          disabled={!description}
                          onClick={() =>
                            window.open(
                              `/app/movements/workOrders/${workOrderId}/complements/${complementId}/partsAndTasks`,
                              '_blank',
                            )
                          }
                        >
                          <ExternalLink style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                      </Tooltip>
                    );
                  },
                },
              ]}
              pagination={false}
            />
          ),
          okText: 'Fechar',
        });
      }
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {benefitDisabled && (
        <ModalLoadingOverlay
          disableLoading
          posAbsolute
          messages={[
            isCustomerTypeInsurance
              ? 'Complemento do tipo cliente Seguradora não tem permissão de benefícios'
              : isCourtesyOrReturn
              ? 'Complemento do tipo Interna (Cortesia) ou Retorno não tem permissão de benefícios'
              : '',
          ]}
        />
      )}

      <ComplementPolicyBenefitsHeader
        hasBenefits={!!benefits.length}
        isSubmiting={creteItemsForBenefits.isLoading}
        onSubmit={onSubmit}
      />

      <Table
        pagination={false}
        dataSource={benefits.concat({
          name: 'insert-benefit',
        } as PolicyBenefitItem)}
        rowKey={(item) => item.sequencialNumber}
        expandable={{
          childrenColumnName: 'materials',
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : undefined,
          onExpandedRowsChange: (keys) =>
            setExpandedRowKey(keys.at(-1) as number),
        }}
        columns={[
          {
            title: 'Benefício / Material',
            render: (_, item, index) => {
              if (item.name === 'insert-material') {
                const selectedMaterialIds = getMaterialIds(item.benefitIndex);

                return (
                  <div style={{ paddingLeft: 24, position: 'relative' }}>
                    <strong style={{ position: 'absolute', left: 24, top: 0 }}>
                      Adicionar material
                    </strong>
                    <BenefitMaterialSelect
                      disabled={isClosedOrCanceled}
                      serviceCode={item.serviceCode}
                      disableMaterialIds={selectedMaterialIds}
                      onChange={(material) =>
                        addMaterial(material, item.benefitIndex as number)
                      }
                    />
                  </div>
                );
              }

              if (item.name === 'insert-benefit')
                return (
                  <>
                    <Group
                      style={{
                        position: 'absolute',
                        left: 4,
                        top: 7,
                      }}
                      gap={10}
                    >
                      <strong>Adicionar benefício</strong>

                      <Switch
                        checkedChildren="Cortesia"
                        unCheckedChildren="Cortesia"
                        onChange={setWithCourtesy}
                        checked={withCoutesy}
                      />
                    </Group>
                    <div style={{ paddingTop: 30 }}>
                      {withCoutesy ? (
                        <AddCourtesyBenefitTable
                          disabled={isClosedOrCanceled}
                          onChange={(benefit) => {
                            addBenefit(
                              benefit
                                ? {
                                    isCourtesy: true,
                                    name: benefit?.name,
                                    sequencialNumber: benefit?.id,
                                    serviceCode: Number(benefit.code),
                                    wasUsedIn: undefined,
                                    isConsumableRequired:
                                      benefit.isConsumableRequired,
                                    isVoucher: benefit.isVoucher,
                                  }
                                : undefined,
                              index,
                            );
                          }}
                          disableBenefitIds={benefitIds}
                        />
                      ) : (
                        <AddBenefitTable
                          disabled={isClosedOrCanceled}
                          onChange={(benefit) =>
                            addBenefit(
                              benefit
                                ? { ...benefit, isCourtesy: false }
                                : undefined,
                              index,
                            )
                          }
                          disableBenefitIds={benefitIds}
                        />
                      )}
                    </div>
                  </>
                );

              if (item.materials) {
                const hasError = errors[index]?.some(
                  (material) =>
                    material.manufacturerId ||
                    material.quantity ||
                    material.subGroupId ||
                    material.isConsumableRequired,
                );

                return (
                  <Group gap={10} wrap="nowrap">
                    <strong style={{ color: hasError ? 'red' : undefined }}>
                      {item.name}
                    </strong>
                    {item.isCourtesy && <StatusTag label="Cortesia" />}
                  </Group>
                );
              }

              return item.name;
            },
          },
          {
            width: 200,
            title: 'Cód. Serviço',
            dataIndex: 'serviceCode',
            render: (serviceCode, item) => {
              if (item.serviceCode && !!item.materials)
                return <strong>{serviceCode}</strong>;
            },
          },
          {
            width: 200,
            dataIndex: 'manufacturerId',
            title: 'Fabricante',
            render: (manufacturerId, item, index) => {
              if (
                item.name === 'insert-benefit' ||
                item.name === 'insert-material' ||
                !!item.materials
              )
                return null;

              const hasError =
                typeof item.benefitIndex === 'number' &&
                typeof index === 'number' &&
                errors[item.benefitIndex] &&
                errors[item.benefitIndex][index]?.manufacturerId;

              return (
                <ManufacturerInfiniteSelectField
                  value={manufacturerId}
                  className={hasError ? 'borderError' : undefined}
                  placeholder="Selecionar fabricante"
                  queryFilter={{ active: true }}
                  disabled={item.disabled || isClosedOrCanceled}
                  onChange={(value) =>
                    updateManufacturer({
                      manufacturerId: value,
                      benefitIndex: item.benefitIndex as number,
                      materialIndex: index,
                    })
                  }
                />
              );
            },
          },
          {
            width: 100,
            dataIndex: 'subGroupId',
            title: 'Subgrupo',
            render: (subGroupId, item, index) => {
              if (
                item.name === 'insert-benefit' ||
                item.name === 'insert-material' ||
                !!item.materials
              )
                return null;

              const material = item as unknown as PolicyBenefitMaterial;

              const hasError =
                typeof item.benefitIndex === 'number' &&
                typeof index === 'number' &&
                errors[item.benefitIndex] &&
                errors[item.benefitIndex][index]?.subGroupId;

              return (
                <Popover
                  content={
                    !material.isInternal ? 'Material não cadastral' : undefined
                  }
                >
                  <SubGroupsSelectField
                    value={subGroupId}
                    className={hasError ? 'borderError' : undefined}
                    disabled={
                      (!!subGroupId && !!material.complementItemId) ||
                      !material.isInternal
                    }
                    placeholder="Selecionar sub grupo"
                    allowClear={false}
                    onChange={(value) =>
                      updateSubGroupId({
                        subGroupId: value,
                        benefitIndex: item.benefitIndex as number,
                        materialIndex: index,
                      })
                    }
                  />
                </Popover>
              );
            },
          },
          {
            width: 100,
            dataIndex: 'quantity',
            title: 'Qtde.',
            render: (quantity, item, index) => {
              if (
                item.name === 'insert-benefit' ||
                item.name === 'insert-material' ||
                !!item.materials
              )
                return null;

              const hasError =
                typeof item.benefitIndex === 'number' &&
                typeof index === 'number' &&
                errors[item.benefitIndex] &&
                errors[item.benefitIndex][index]?.quantity;

              return (
                <InputDecimal
                  value={quantity}
                  className={hasError ? 'borderError' : undefined}
                  placeholder="0,00"
                  allowClear
                  key={item.serviceCode}
                  disabled={item.disabled || isClosedOrCanceled}
                  min={0}
                  onChange={(value) =>
                    updateQuantity({
                      quantity: value,
                      benefitIndex: item.benefitIndex as number,
                      materialIndex: index,
                    })
                  }
                />
              );
            },
          },
          {
            width: 100,
            title: <ToReceiveColumnTitle />,
            render: (_, item) =>
              item.name && item.materials ? (
                <strong>{'Sem valor'}</strong>
              ) : null,
          },
          {
            width: 60,
            title: 'Ação',
            fixed: 'right',
            align: 'right',
            render: (_, item, index) => {
              if (
                item.name === 'insert-material' ||
                item.name === 'insert-benefit'
              )
                return null;

              if (item.materials) {
                return (
                  <RemoveBenefitTable
                    disabled={item.disabled || isClosedOrCanceled}
                    onClick={() =>
                      confirmToRemove({
                        benefitIndex: index,
                        complementItemId: item.complementItemId,
                      })
                    }
                  />
                );
              }

              if (!item.materials) {
                return (
                  <RemoveBenefitTable
                    disabled={item.disabled || isClosedOrCanceled}
                    onClick={() =>
                      confirmToRemove({
                        materialIndex: index,
                        benefitIndex: item.benefitIndex as number,
                        complementItemId: item.complementItemId,
                      })
                    }
                  />
                );
              }
            },
          },
        ]}
      />
    </div>
  );
}
